import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileBarChart2, Eye, Send, Download, Loader2, Plus, X, Users } from "lucide-react";
import { SettingsSection } from "@/components/SettingsSection";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls = "rounded-xl border border-sand-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 disabled:opacity-60";
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const ROLE = { company_admin: "Administrator", regional_manager: "Community manager", hoa_manager: "Community manager" };
const SCOPE = { firm: "whole firm", region: "their region", mine: "their communities" };

// Portfolio Concierge Report — one document above the community, scheduled per firm. Default off.
export function PortfolioReportCard({ companyId }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [extra, setExtra] = useState("");

  const load = useCallback(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/report/schedule`).then((r) => setS(r.data)).catch(() => toast.error("Couldn't load the portfolio report settings"));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    try { const r = await api.put(`/companies/${companyId}/report/schedule`, patch); setS(r.data); toast.success("Saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
  };
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "preview") {
        const r = await api.post(`/companies/${companyId}/report/preview`, { frequency: s.frequency });
        setPreview(r.data);
      } else if (kind === "test") {
        const tid = toast.loading("Building your report and emailing it — about 10 seconds…");
        const r = await api.post(`/companies/${companyId}/report/send-now`, { test: true, frequency: s.frequency });
        toast.dismiss(tid);
        r.data.sent ? toast.success(`Sent a test to ${r.data.to}`) : toast.error(r.data.note || "Couldn't send");
      } else if (kind === "pdf") {
        const tid = toast.loading("Rendering the PDF…");
        const r = await api.post(`/companies/${companyId}/report/download`, { frequency: s.frequency }, { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a"); a.href = url; a.download = "portfolio-report.pdf"; a.click(); URL.revokeObjectURL(url);
        toast.dismiss(tid);
      } else if (kind === "blast") {
        const r = await api.post(`/companies/${companyId}/report/send-now`, { test: false, frequency: s.frequency });
        toast.success(`Sending to ${r.data.queued} reader${r.data.queued === 1 ? "" : "s"} now`);
      }
    } catch (e) { toast.dismiss(); toast.error(e?.response?.data?.detail || "That didn't work — try again"); }
    finally { setBusy(""); }
  };
  const addExtra = () => {
    const em = extra.trim().toLowerCase();
    if (!em) return;
    save({ extra_recipients: [...(s.extra_recipients || []), { email: em }] });
    setExtra("");
  };

  if (!s) return <div className="rounded-2xl border border-sand-200 bg-white p-5 flex items-center gap-2 text-sm text-muted-foreground" data-testid="portfolio-report-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const ro = !s.can_edit;
  const on = s.frequency !== "off";
  const readers = s.readers || [];
  const live = readers.filter((r) => !r.suppressed).length + (s.extra_recipients || []).length;

  return (
    <SettingsSection id="portfolio-report-card" icon={FileBarChart2} title="Portfolio Concierge Report" defaultOpen
      badges={[on ? { text: s.frequency, ok: true } : { text: "off" }]}
      summary={on ? `${s.frequency === "weekly" ? `Every ${DAYS[s.day_of_week]}` : `Day ${s.day_of_month} of each month`} at ${s.hour}:00 ${s.effective_timezone} · ${live} reader${live === 1 ? "" : "s"} · league table of all ${s.communities} communities` : `Off — one email and PDF for the whole portfolio: every community's numbers on one page, sorted by what needs attention.`}>
      <p className="text-sm text-muted-foreground -mt-1">
        One document above the community: a league table of every community sorted by what needs attention, each with its manager's name and ▲/▼ vs the prior period, portfolio-wide "waiting on you", the rolled-up numbers, a scorecard per community and a by-manager view.
        Each reader gets <span className="font-semibold text-ink">their own scope</span> — administrators the whole firm, community managers their communities. Community boards keep their own Concierge Report unchanged.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block text-xs font-semibold text-muted-foreground mb-1">Cadence</span>
          <select data-testid="portfolio-report-frequency" disabled={ro} value={s.frequency} onChange={(e) => save({ frequency: e.target.value })} className={inputCls}>
            <option value="off">Off</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
          </select>
        </label>
        {s.frequency === "weekly" && (
          <label className="text-sm"><span className="block text-xs font-semibold text-muted-foreground mb-1">Day</span>
            <select data-testid="portfolio-report-day" disabled={ro} value={s.day_of_week} onChange={(e) => save({ day_of_week: Number(e.target.value) })} className={inputCls}>
              {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select></label>
        )}
        {s.frequency === "monthly" && (
          <label className="text-sm"><span className="block text-xs font-semibold text-muted-foreground mb-1">Day of month</span>
            <select data-testid="portfolio-report-dom" disabled={ro} value={s.day_of_month} onChange={(e) => save({ day_of_month: Number(e.target.value) })} className={inputCls}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
            </select></label>
        )}
        {on && (
          <label className="text-sm"><span className="block text-xs font-semibold text-muted-foreground mb-1">Hour ({s.effective_timezone})</span>
            <select data-testid="portfolio-report-hour" disabled={ro} value={s.hour} onChange={(e) => save({ hour: Number(e.target.value) })} className={inputCls}>
              {Array.from({ length: 24 }, (_, i) => i).map((h) => <option key={h} value={h}>{h}:00</option>)}
            </select></label>
        )}
        <label className="flex items-center gap-2 text-sm pb-2">
          <input type="checkbox" data-testid="portfolio-report-attach-pdf" disabled={ro} checked={!!s.attach_pdf} onChange={(e) => save({ attach_pdf: e.target.checked })} className="h-4 w-4 accent-redline-500" /> Attach PDF
        </label>
        <label className="flex items-center gap-2 text-sm pb-2">
          <input type="checkbox" data-testid="portfolio-report-pending" disabled={ro} checked={!!s.include_pending} onChange={(e) => save({ include_pending: e.target.checked })} className="h-4 w-4 accent-redline-500" /> Include "waiting on you"
        </label>
        <label className="flex items-center gap-2 text-sm pb-2" title="Community managers who run two or more of your communities get a 'my communities' rollup">
          <input type="checkbox" data-testid="portfolio-report-managers" disabled={ro} checked={!!s.include_managers} onChange={(e) => save({ include_managers: e.target.checked })} className="h-4 w-4 accent-redline-500" /> Also send multi-community managers their own rollup
        </label>
      </div>

      <div className="rounded-xl border border-sand-200 bg-sand-50 p-3" data-testid="portfolio-report-readers">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-2"><Users className="h-3.5 w-3.5" /> Who receives it</div>
        {readers.length === 0 && <div className="text-sm text-muted-foreground">No administrators or community managers with communities yet.</div>}
        <ul className="grid gap-1 sm:grid-cols-2">
          {readers.map((r) => (
            <li key={r.email} className="text-sm flex items-baseline gap-1.5" data-testid={`portfolio-report-reader-${r.email}`}>
              <span className={`font-semibold ${r.suppressed ? "line-through text-ink/40" : "text-ink"}`}>{r.name}</span>
              <span className="text-xs text-muted-foreground">{ROLE[r.role] || r.role} · {SCOPE[r.scope]} ({r.communities})</span>
              {r.suppressed && <span className="text-[10px] font-bold text-amber-700">email bouncing</span>}
            </li>
          ))}
        </ul>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {(s.extra_recipients || []).map((e) => (
            <span key={e.email} className="inline-flex items-center gap-1 rounded-full border border-sand-200 bg-white px-2.5 py-1 text-xs" data-testid={`portfolio-report-extra-${e.email}`}>
              {e.email}
              {!ro && <button type="button" aria-label={`Remove ${e.email}`} onClick={() => save({ extra_recipients: (s.extra_recipients || []).filter((x) => x.email !== e.email) })} className="text-ink/40 hover:text-danger-600"><X className="h-3 w-3" /></button>}
            </span>
          ))}
          {!ro && (
            <span className="inline-flex items-center gap-1">
              <input data-testid="portfolio-report-extra-input" value={extra} onChange={(e) => setExtra(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addExtra()} placeholder="Add an email (owner, board of the firm…)" className={inputCls + " w-72 py-1.5 text-xs"} />
              <button type="button" data-testid="portfolio-report-extra-add" onClick={addExtra} className="h-8 w-8 rounded-full border border-sand-200 bg-white flex items-center justify-center hover:border-ink"><Plus className="h-3.5 w-3.5" /></button>
            </span>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">Extra emails get the whole-firm view. Anyone can mute their copy from their notification preferences.</p>
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <Btn testid="portfolio-report-preview" icon={Eye} busy={busy === "preview"} onClick={() => run("preview")}>Preview my report</Btn>
        <Btn testid="portfolio-report-test" icon={Send} busy={busy === "test"} onClick={() => run("test")}>Email me a test</Btn>
        <Btn testid="portfolio-report-pdf" icon={Download} busy={busy === "pdf"} onClick={() => run("pdf")}>Download PDF</Btn>
        {!ro && <Btn testid="portfolio-report-send-all" icon={Send} busy={busy === "blast"} onClick={() => { if (window.confirm(`Send the ${s.frequency === "weekly" ? "weekly" : "monthly"} portfolio report to ${live} reader${live === 1 ? "" : "s"} now?`)) run("blast"); }} primary>Send to everyone now</Btn>}
        {s.last_sent_at && <span className="text-xs text-muted-foreground self-center" data-testid="portfolio-report-last-sent">Last sent {new Date(s.last_sent_at).toLocaleString()}</span>}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0" data-testid="portfolio-report-preview-dialog" aria-describedby={undefined}>
          <DialogHeader className="px-5 pt-4 pb-2"><DialogTitle className="text-base">{preview?.subject}</DialogTitle></DialogHeader>
          {preview && <iframe title="Portfolio report preview" srcDoc={preview.html} className="w-full h-[78vh] border-0 bg-white" data-testid="portfolio-report-preview-frame" />}
        </DialogContent>
      </Dialog>
    </SettingsSection>
  );
}

function Btn({ icon: Icon, busy, onClick, children, testid, primary }) {
  return (
    <button type="button" data-testid={testid} disabled={busy} onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors disabled:opacity-60 ${primary ? "bg-ink text-white hover:bg-ink/85" : "border border-sand-200 bg-white text-ink hover:border-ink"}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />} {children}
    </button>
  );
}
